import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

/*
 * Dot — small status/tone indicator.
 *
 * Examples:
 *   <Dot tone="green" />
 *   <Dot tone="amber" pulse />
 *   <Dot tone="brand" size="md" />
 *
 * `sm` (6×6, `--dot-xs`, default) is the inline status spec; `md` (7×7,
 * `--dot-sm`) is the section-head dot. The component does not accept a
 * `className` prop — every visual decision lives in the variant table here.
 */
const dotVariants = cva('relative inline-block flex-shrink-0 rounded-pill', {
  variants: {
    tone: {
      green: 'bg-tone-green-fg',
      amber: 'bg-tone-amber-fg',
      red: 'bg-tone-red-fg',
      gray: 'bg-tone-gray-fg',
      ai: 'bg-ai',
      brand: 'bg-primary',
    },
    size: {
      sm: 'h-dot-xs w-dot-xs',
      md: 'h-dot-sm w-dot-sm',
    },
  },
  defaultVariants: { tone: 'gray', size: 'sm' },
});

export type DotVariantProps = VariantProps<typeof dotVariants>;

export interface DotProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'className'>, DotVariantProps {
  pulse?: boolean;
}

export const Dot = forwardRef<HTMLSpanElement, DotProps>(function Dot(
  { tone, size, pulse = false, ...props },
  ref,
) {
  return (
    <span ref={ref} aria-hidden className={dotVariants({ tone, size })} {...props}>
      {pulse ? (
        <span
          aria-hidden
          className="absolute -inset-0.5 rounded-pill bg-inherit opacity-40 motion-safe:animate-pulse-dot"
        />
      ) : null}
    </span>
  );
});
